import {
  Chip,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import i18n from 'i18n';
import React from 'react';
import {
  getDefaultAvailableOperators,
  SelectedFilter,
  SelectOption,
} from 'utils/filters';
import ConditionOperatorSelect from '../ConditionOperatorSelect';
import styles from './styles';

interface Props {
  filter: SelectedFilter;
  onDeleteFilter: (filter: SelectedFilter) => void;
  onUpdateFilter: (filter: SelectedFilter) => void;
}

const MultipleFilter = ({ filter, onDeleteFilter, onUpdateFilter }: Props) => {
  const classes = makeStyles(styles)();
  const [focus, setFocus] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  const getInitialValues = React.useCallback(() => {
    if (Array.isArray(filter.value)) {
      return filter.value;
    }
    return filter.value?.split(',') || [];
  }, [filter.value]);

  const [values, setValues] = React.useState(getInitialValues());

  React.useEffect(() => {
    setValues(getInitialValues());
  }, [getInitialValues]);

  const handleChange = value => {
    setValues(value);
    onUpdateFilter({
      ...filter,
      value: filter.isSelect
        ? value.map((option: SelectOption) => option.value)
        : value,
    });
  };

  const handleOnBlur = event => {
    setFocus(false);

    const value = event.target.value.trim();

    if (value !== '') {
      handleChange([...values, value]);
    }
  };

  const getLimitedTags = tags => {
    const LIMIT = 5;

    const more = tags.length - LIMIT;
    let limitedTags = tags;

    if (!focus && more > 0) {
      limitedTags = limitedTags.splice(0, LIMIT);

      limitedTags.push(
        <Typography
          key={limitedTags.length}
          className={classes.moreText}
        >{`+${more}`}</Typography>
      );
    }

    return limitedTags;
  };

  return filter.isSelect ? (
    <Autocomplete
      id="multiple-filter"
      open={open}
      multiple
      filterSelectedOptions
      size="small"
      className={classes.autoComplete}
      value={filter.selectOptions?.filter(option =>
        values.includes(option.value)
      )}
      options={filter.selectOptions}
      getOptionSelected={(option, value) => option.value === value.value}
      filterOptions={createFilterOptions({
        stringify: option => i18n.t(option.label),
      })}
      noOptionsText={i18n.t('NO_OPTIONS_KEY')}
      onChange={(_, value) => handleChange(value)}
      getOptionLabel={option => i18n.t(option.label)}
      renderTags={(value, getTagsProps) => {
        const tags = value.map((option: SelectOption, index: number) => (
          <Chip
            key={index}
            variant="outlined"
            label={i18n.t(option.label)}
            {...getTagsProps({ index })}
          />
        ));

        return getLimitedTags(tags);
      }}
      renderInput={params => (
        <TextField
          {...params}
          id={filter.field}
          onFocus={() => {
            setFocus(true);
            setOpen(true);
          }}
          onBlur={() => {
            setFocus(false);
            setOpen(false);
          }}
          fullWidth
          variant="outlined"
          label={i18n.t(filter.label)}
          placeholder={i18n.t('ADD_KEY')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <ConditionOperatorSelect
                    options={
                      filter.availableOperators ||
                      getDefaultAvailableOperators(filter)
                    }
                    value={filter.operator}
                    onChange={operator =>
                      onUpdateFilter({ ...filter, operator })
                    }
                  />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <InputAdornment position="end" className={classes.endAdornment}>
                <Close onClick={() => onDeleteFilter(filter)} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  ) : (
    <Autocomplete
      id="multiple-filter"
      multiple
      freeSolo
      size="small"
      className={classes.autoComplete}
      value={values}
      options={[]}
      noOptionsText={i18n.t('NO_OPTIONS_KEY')}
      onChange={(_, value: string[]) => {
        handleChange(value.filter(item => Boolean(item.trim())));
      }}
      renderTags={(value, getTagsProps) => {
        const tags = value.map((option: string, index: number) => (
          <Chip
            key={index}
            variant="outlined"
            label={option}
            {...getTagsProps({ index })}
          />
        ));

        return getLimitedTags(tags);
      }}
      renderInput={params => (
        <TextField
          {...params}
          id={filter.field}
          onFocus={() => setFocus(true)}
          onBlur={handleOnBlur}
          variant="outlined"
          placeholder={i18n.t('ADD_KEY')}
          label={i18n.t(filter.label)}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <ConditionOperatorSelect
                    options={
                      filter.availableOperators ||
                      getDefaultAvailableOperators(filter)
                    }
                    value={filter.operator}
                    onChange={operator =>
                      onUpdateFilter({ ...filter, operator })
                    }
                  />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <InputAdornment position="end" className={classes.endAdornment}>
                <Close onClick={() => onDeleteFilter(filter)} />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
};

export default MultipleFilter;
